import { type ProjectParticipant, type ResponsibleParty } from '@consigli/types';
import { FC } from 'react';

/**
 * Derive avatar initials from a display name, falling back to the email local part.
 * Handles the "Last, First" format used by AAD directory names.
 */
export function getInitials(name: string | null | undefined, email?: string | null): string {
  const trimmedName = name?.trim();
  if (trimmedName) {
    // Handle "Last, First" format
    if (trimmedName.includes(',')) {
      const [last, first] = trimmedName.split(',').map((p) => p.trim());
      return ((first?.[0] ?? '') + (last?.[0] ?? '')).toUpperCase();
    }
    const words = trimmedName.split(/\s+/).filter(Boolean);
    if (words.length >= 2) {
      return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    }
    return trimmedName.slice(0, 2).toUpperCase();
  }
  const safeEmail = email ?? '';
  const local = safeEmail.split('@')[0] ?? safeEmail;
  const parts = local.split(/[._-]/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return local.slice(0, 2).toUpperCase();
}

/**
 * Join a list of responsible parties into a comma-separated display string.
 * Used by exports, copy, and email where a plain string is needed.
 */
export const formatResponsibleParties = (parties: ResponsibleParty[] | null | undefined): string =>
  (parties ?? [])
    .map((p) => p.name?.trim())
    .filter((name): name is string => Boolean(name))
    .join(', ');

/**
 * Build a ResponsibleParty for optimistic local state from a reassignment
 * target (a participant id or a custom name), resolving the display name.
 */
export const resolveResponsibleParty = (
  target: { meetingUserId?: string | null; customPartyName?: string | null },
  participants: ProjectParticipant[],
): ResponsibleParty => {
  if (target.meetingUserId) {
    const participant = participants.find((p) => p.id === target.meetingUserId);
    return {
      meetingUserId: target.meetingUserId,
      customPartyId: null,
      name: participant?.name ?? participant?.email ?? null,
    };
  }
  return { meetingUserId: null, customPartyId: null, name: target.customPartyName ?? null };
};

export const collectCustomParties = (
  parties: (ResponsibleParty | null | undefined)[],
): ResponsibleParty[] => {
  const seen = new Set<string>();
  const result: ResponsibleParty[] = [];
  for (const party of parties) {
    if (!party || party.meetingUserId) continue;
    const name = party.name?.trim();
    if (!name) continue;
    const key = party.customPartyId ? `c:${party.customPartyId}` : `n:${name.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push({ meetingUserId: null, customPartyId: party.customPartyId ?? null, name });
  }
  return result;
};

interface AvatarProps {
  name?: string | null;
  /** Optional — only used as an initials/label fallback when `name` is absent. */
  email?: string | null;
  className?: string;
}

export const Avatar: FC<AvatarProps> = ({ name, email, className = '' }) => (
  <div
    className={`w-9 h-9 bg-brand-light text-brand rounded-full text-sm font-semibold flex items-center justify-center ring-2 ring-white flex-shrink-0 ${className}`}
    title={name ?? email ?? ''}
    aria-label={name ?? email ?? ''}
  >
    {getInitials(name, email)}
  </div>
);
